import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import styled from 'styled-components'
import {getBoxShadow} from 'utils/commons'
import {MenuProps} from './MenuProps'
import {Pressable} from '../../pressables'

const StyledContainer = styled.div`
  position: relative;
  width: fit-content;
  height: fit-content;
`

const StyledContentContainer = styled.div`
  ${({theme}) => ({
    backgroundColor: theme.white_1,
    boxShadow: getBoxShadow(theme, {verticalOffset: 8}),
  })}
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: absolute;
  min-width: 200px;
  padding: 8px 0px;
  border-radius: 8px;
  overflow: hidden;
  z-index: 10;
`

export default function Menu({
  directionVertical = 'bottom',
  directionHorizontal = 'right',
  contentContainerStyle: baseContentContainerStyle,
  stateToggle,
  offset = 20,
  renderContent,
  children,
  ...props
}: MenuProps) {
  const [isVisible, setVisible] = stateToggle
  const refContent = useRef<HTMLDivElement>(null)

  const toggle = useCallback(() => setVisible((prev) => !prev), [setVisible])

  const contentContainerStyle = useMemo<CSSProperties>(
    () => ({
      ...baseContentContainerStyle,
      ...(directionVertical === 'bottom'
        ? {top: '100%', transform: `translate(0, ${offset}px)`}
        : {bottom: '100%', transform: `translate(0, -${offset}px)`}),
      ...(directionHorizontal === 'right' ? {left: 0} : {right: 0}),
    }),
    [baseContentContainerStyle, directionVertical, directionHorizontal, offset],
  )

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        refContent.current &&
        // @ts-ignore
        !refContent.current.contains(event.target)
      ) {
        setVisible(false)
      }
    },
    [setVisible],
  )

  const handleEventListener = useCallback(() => {
    if (isVisible) {
      document.addEventListener('click', handleClickOutside)

      return () => document.removeEventListener('click', handleClickOutside)
    }

    return undefined
  }, [isVisible, handleClickOutside])

  const handleRenderContent = useMemo(
    () =>
      isVisible && (
        <StyledContentContainer ref={refContent} style={contentContainerStyle}>
          {renderContent()}
        </StyledContentContainer>
      ),
    [isVisible, contentContainerStyle, renderContent],
  )

  useEffect(handleEventListener, [handleEventListener])

  return (
    <StyledContainer {...props}>
      <Pressable onClick={toggle}>{children}</Pressable>
      {handleRenderContent}
    </StyledContainer>
  )
}
